import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CaretDown, CaretRight, ShieldCheck, X } from 'phosphor-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import { IData } from '../../types/shared';
import { diaDoLog, horaDoLog, iniciais, tituloDoDia } from './acessoApi';
import { AZUL, Busca, Cartao, Etiqueta, Eyebrow, MONO, Pilula, TEXTO, TEXTO_SUAVE } from './ui';

interface IMudancaLista {
  entrou: (string | number)[];
  saiu: (string | number)[];
}
interface IMudancaValor {
  antes: unknown;
  depois: unknown;
}
type Nomes = Partial<Record<'empresa' | 'grupo' | 'usuario', Record<string, string>>>;

export interface IRegistroDeAcesso {
  id: number;
  dt_criacao: string;
  ator_id: number | null;
  ator_nome: string | null;
  alvo_tipo: 'grupo' | 'usuario';
  alvo_id: number;
  alvo_nome: string | null;
  acao: string;
  /** por campo do acesso; a chave `_nomes` traz os rótulos dos ids citados */
  mudancas: Record<string, unknown>;
  resumo: string;
}

const CAMPOS: Record<string, { rotulo: string; tipo?: 'empresa' | 'grupo' | 'usuario' }> = {
  nome: { rotulo: 'Nome' },
  ativo: { rotulo: 'Ativo' },
  nivel: { rotulo: 'Nível' },
  grupo_principal: { rotulo: 'Grupo principal', tipo: 'grupo' },
  adicionais: { rotulo: 'Grupos adicionais', tipo: 'grupo' },
  telas: { rotulo: 'Telas e ações' },
  gerais: { rotulo: 'Permissões gerais' },
  liberadas: { rotulo: 'Exceções liberadas' },
  bloqueadas: { rotulo: 'Exceções bloqueadas' },
  carteira: { rotulo: 'Grupos de empresas' },
  empresas: { rotulo: 'Empresas avulsas', tipo: 'empresa' },
  membros: { rotulo: 'Membros', tipo: 'usuario' },
};

const ACAO_DA_TELA = /^(.+):(view|create|edit|delete)$/;
const VERBO: Record<string, string> = {
  view: 'ver',
  create: 'criar',
  edit: 'editar',
  delete: 'excluir',
};

const legivel = (campo: string, valor: unknown, nomes?: Nomes): string => {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (typeof valor === 'boolean') return valor ? 'sim' : 'não';
  const tipo = CAMPOS[campo]?.tipo;
  if (tipo && nomes?.[tipo]?.[String(valor)]) return nomes[tipo]![String(valor)];
  if (tipo) return `${tipo === 'empresa' ? 'empresa' : tipo} #${valor}`;
  const m = ACAO_DA_TELA.exec(String(valor));
  if (m) return `${m[1]} · ${VERBO[m[2]] ?? m[2]}`;
  return String(valor);
};

const historicoApi = async (filtro: Record<string, unknown>, page: number) => {
  const res = await api.get<IData<IRegistroDeAcesso[]>>('/api/v1/acesso/historico/', {
    params: { page, size: 30, ...filtro },
  });
  return res.data.data;
};

/** chave do backend com os rótulos dos ids citados (não é campo de acesso) */
const CHAVE_NOMES = '_nomes';

function Detalhe({ registro }: { registro: IRegistroDeAcesso }) {
  const nomes = registro.mudancas[CHAVE_NOMES] as Nomes | undefined;
  return (
    <Flex direction="column" gap="10px" mt="10px" pl={{ base: 0, md: '44px' }}>
      {Object.entries(registro.mudancas)
        .filter(([campo]) => !campo.startsWith('_'))
        .map(([campo, valor]) => {
          const m = valor as IMudancaLista | IMudancaValor;
          return (
            <Box key={campo}>
              <Eyebrow>{(CAMPOS[campo]?.rotulo ?? campo).toUpperCase()}</Eyebrow>
              {'entrou' in m ? (
                <Flex gap="6px" wrap="wrap" mt="4px">
                  {m.entrou.map(v => (
                    <Etiqueta key={`+${v}`} tom="sucesso">
                      + {legivel(campo, v, nomes)}
                    </Etiqueta>
                  ))}
                  {m.saiu.map(v => (
                    <Etiqueta key={`-${v}`} tom="perigo">
                      − {legivel(campo, v, nomes)}
                    </Etiqueta>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="13px" color={TEXTO} mt="2px">
                  {legivel(campo, m.antes, nomes)} →{' '}
                  <strong>{legivel(campo, m.depois, nomes)}</strong>
                </Text>
              )}
            </Box>
          );
        })}
    </Flex>
  );
}

/** Aba Logs → "Mudanças de permissão": quem mudou o quê em grupos e usuários. */
export function HistoricoDePermissoes() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const alvoTipo = params.get('alvo_tipo') as 'grupo' | 'usuario' | null;
  const alvoId = Number(params.get('alvo_id')) || undefined;
  const alvoNome = params.get('alvo_nome');
  const [digitado, setDigitado] = useState('');
  const [busca, setBusca] = useState('');
  const [abertos, setAbertos] = useState<Set<number>>(new Set());
  useEffect(() => {
    const t = setTimeout(() => setBusca(digitado.trim()), 400);
    return () => clearTimeout(t);
  }, [digitado]);

  const mudar = (novo: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    Object.entries(novo).forEach(([k, v]) => (v === null ? p.delete(k) : p.set(k, v)));
    setParams(p, { replace: true });
  };

  const filtro = {
    alvo_tipo: alvoTipo ?? undefined,
    alvo_id: alvoId,
    busca: busca || undefined,
  };
  const consulta = useInfiniteQuery({
    queryKey: ['central', 'historico-acesso', filtro],
    queryFn: ({ pageParam }) => historicoApi(filtro, pageParam),
    initialPageParam: 1,
    getNextPageParam: ultima => ultima.next_page ?? undefined,
  });
  const registros = useMemo(
    () => (consulta.data?.pages ?? []).flatMap(p => p.items),
    [consulta.data],
  );
  const total = consulta.data?.pages[0]?.total ?? 0;
  const porDia = useMemo(() => {
    const dias: { dia: string; titulo: string; itens: IRegistroDeAcesso[] }[] = [];
    registros.forEach(r => {
      const dia = diaDoLog(r.dt_criacao);
      const atual = dias[dias.length - 1];
      if (atual && atual.dia === dia) atual.itens.push(r);
      else dias.push({ dia, titulo: tituloDoDia(r.dt_criacao), itens: [r] });
    });
    return dias;
  }, [registros]);

  const abrirAlvo = (r: IRegistroDeAcesso) =>
    navigate(
      r.alvo_tipo === 'usuario'
        ? `/configuracoes/permissoes?usuario=${r.alvo_id}`
        : `/configuracoes/permissoes?grupo=${r.alvo_id}&aba=telas`,
    );

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="18px 20px" display="flex" flexDirection="column" gap="14px">
        <Flex align="flex-start" justify="space-between" gap="12px" wrap="wrap">
          <Box>
            <Eyebrow>HISTÓRICO DE PERMISSÕES</Eyebrow>
            <Text fontSize="22px" lineHeight="28px" fontWeight={700} letterSpacing="-.3px">
              Quem mudou o quê no acesso
            </Text>
            <Text fontSize="13px" color="#475569">
              Telas, permissões gerais, carteira, empresas avulsas, grupos e exceções de cada grupo
              e usuário. Clique numa linha para ver o que entrou e o que saiu.
            </Text>
          </Box>
          <Box textAlign="right">
            <Text fontFamily={MONO} fontSize="22px" fontWeight={600} color={TEXTO}>
              {consulta.isLoading ? '…' : total.toLocaleString('pt-BR')}
            </Text>
            <Text fontSize="12px" color={TEXTO_SUAVE}>
              mudanças neste filtro
            </Text>
          </Box>
        </Flex>
        <Flex gap="10px" wrap="wrap" align="center">
          <Box flex="1 1 280px" maxW="420px">
            <Busca
              valor={digitado}
              onChange={setDigitado}
              placeholder="Quem fez, grupo ou usuário"
            />
          </Box>
          <Flex gap="6px" wrap="wrap">
            {(
              [
                [null, 'Tudo'],
                ['grupo', 'Grupos'],
                ['usuario', 'Usuários'],
              ] as const
            ).map(([valor, rotulo]) => (
              <Pilula
                key={rotulo}
                ativa={alvoTipo === valor && (valor !== null || !alvoId)}
                onClick={() => mudar({ alvo_tipo: valor, alvo_id: null, alvo_nome: null })}
              >
                {rotulo}
              </Pilula>
            ))}
          </Flex>
          {alvoId && (
            <Flex
              align="center"
              gap="6px"
              h="34px"
              pl="12px"
              pr="6px"
              borderRadius="999px"
              bg="#DFF3FF"
              color="#075985"
              fontSize="13px"
              fontWeight={600}
            >
              Só {alvoNome ?? `${alvoTipo} ${alvoId}`}
              <Box
                as="button"
                type="button"
                aria-label="Ver todos"
                onClick={() => mudar({ alvo_tipo: null, alvo_id: null, alvo_nome: null })}
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="22px"
                h="22px"
                borderRadius="50%"
                _hover={{ bg: 'rgba(7,89,133,.12)' }}
              >
                <X size={12} />
              </Box>
            </Flex>
          )}
        </Flex>
      </Cartao>

      <Cartao p={0} overflow="hidden">
        {consulta.isLoading && (
          <Flex justify="center" py={10}>
            <Spinner color={AZUL} />
          </Flex>
        )}
        {consulta.isError && (
          <Text p="20px" fontSize="13px" color="#991B1B">
            Não foi possível carregar o histórico de permissões.
          </Text>
        )}
        {!consulta.isLoading && !consulta.isError && registros.length === 0 && (
          <Flex direction="column" align="center" gap="8px" py={12} color={TEXTO_SUAVE}>
            <ShieldCheck size={32} />
            <Text fontSize="13px">
              Nenhuma mudança de permissão registrada {alvoId ? 'para este alvo' : 'ainda'}.
            </Text>
          </Flex>
        )}
        {porDia.map(d => (
          <Box key={d.dia}>
            <Flex
              align="center"
              justify="space-between"
              p="12px 20px 8px"
              bg="rgba(248,250,252,.7)"
              position="sticky"
              top={0}
              zIndex={1}
            >
              <Text fontSize="12.5px" fontWeight={700} color="#334155">
                {d.titulo}
              </Text>
              <Text fontFamily={MONO} fontSize="11px" color={TEXTO_SUAVE}>
                {d.itens.length}
              </Text>
            </Flex>
            {d.itens.map(r => {
              const aberto = abertos.has(r.id);
              return (
                <Box key={r.id} p="10px 20px" borderTop="1px solid rgba(15,23,42,.05)">
                  <Flex
                    as="button"
                    type="button"
                    w="100%"
                    textAlign="left"
                    align="center"
                    gap="12px"
                    wrap="wrap"
                    onClick={() =>
                      setAbertos(s => {
                        const n = new Set(s);
                        if (n.has(r.id)) n.delete(r.id);
                        else n.add(r.id);
                        return n;
                      })
                    }
                  >
                    {aberto ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    <Text
                      fontFamily={MONO}
                      fontSize="12px"
                      color={TEXTO_SUAVE}
                      w="44px"
                      flex="none"
                    >
                      {horaDoLog(r.dt_criacao)}
                    </Text>
                    <Flex
                      w="28px"
                      h="28px"
                      borderRadius="50%"
                      bg="#DFF3FF"
                      color="#075985"
                      align="center"
                      justify="center"
                      fontSize="11px"
                      fontWeight={700}
                      flex="none"
                    >
                      {iniciais(r.ator_nome ?? '?')}
                    </Flex>
                    <Box flex="1 1 260px" minW={0}>
                      <Text fontSize="13.5px" color={TEXTO} noOfLines={1}>
                        <strong>{r.ator_nome ?? 'Alguém'}</strong> mudou{' '}
                        {r.alvo_tipo === 'grupo' ? 'o grupo' : 'o usuário'}{' '}
                        <strong>{r.alvo_nome ?? `#${r.alvo_id}`}</strong>
                      </Text>
                      <Text fontSize="12px" color={TEXTO_SUAVE} noOfLines={1}>
                        {r.resumo}
                      </Text>
                    </Box>
                    <Etiqueta tom={r.alvo_tipo === 'grupo' ? 'info' : 'neutro'}>
                      {r.alvo_tipo === 'grupo' ? 'grupo' : 'usuário'}
                    </Etiqueta>
                  </Flex>
                  {aberto && (
                    <>
                      <Detalhe registro={r} />
                      <Flex gap="8px" mt="10px" pl={{ base: 0, md: '44px' }}>
                        <Button
                          size="xs"
                          variant="ghost"
                          borderRadius="999px"
                          onClick={() => abrirAlvo(r)}
                        >
                          Abrir {r.alvo_tipo === 'grupo' ? 'o grupo' : 'o usuário'}
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          borderRadius="999px"
                          onClick={() =>
                            mudar({
                              alvo_tipo: r.alvo_tipo,
                              alvo_id: String(r.alvo_id),
                              alvo_nome: r.alvo_nome,
                            })
                          }
                        >
                          Só este {r.alvo_tipo === 'grupo' ? 'grupo' : 'usuário'}
                        </Button>
                      </Flex>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
        {consulta.hasNextPage && (
          <Flex justify="center" p="12px 20px" borderTop="1px solid rgba(15,23,42,.06)">
            <Button
              size="sm"
              variant="outline"
              borderRadius="999px"
              bg="white"
              isLoading={consulta.isFetchingNextPage}
              onClick={() => consulta.fetchNextPage()}
            >
              Carregar mais
            </Button>
          </Flex>
        )}
      </Cartao>
    </Flex>
  );
}
